import type { RootState } from 'RootTypes';
import _ from 'lodash';

import type { UserID, PlayerID, VideoClipID, TeamID } from '@common/ids';

import type { Player } from './types';
import { NAMESPACE } from './types';

const isPlayerOnTeam = (teamID: TeamID) => (p: Player) =>
    p.teamID === teamID && ['active', 'removed'].includes(p.status);
const isActivePlayer = (p: Player) => p.status === 'active';
const isPlayerWithID = (playerID: PlayerID) => (p: Player) => p.id === playerID;

export const getPlayers = (teamID: TeamID) => (state: RootState) =>
    _.get(state[NAMESPACE].players, teamID, []).filter(isPlayerOnTeam(teamID));

export const getActivePlayers = (teamID: TeamID) => (state: RootState) =>
    getPlayers(teamID)(state).filter(isActivePlayer);

export const getActivePlayer = (teamID: TeamID, playerID: PlayerID) => (state: RootState) =>
    getActivePlayers(teamID)(state).find(isPlayerWithID(playerID));

export const getPlayer = (teamID: TeamID, playerID: PlayerID) => (state: RootState) =>
    getPlayers(teamID)(state).find(isPlayerWithID(playerID));

export const getTeamMembers = (teamID: TeamID) => (state: RootState) =>
    state[NAMESPACE].teamMemberUsers.filter((t) => t.teamID === teamID);

export const getTeamMember = (teamID: TeamID, userID: UserID) => (state: RootState) =>
    getTeamMembers(teamID)(state).find((tm) => tm.userID === userID);

export const getLoggedInTeamMember = (teamID: TeamID) => (state: RootState) =>
    getTeamMembers(teamID)(state).find((tm) => tm.isLoggedInUser);

export const getRolloverOptions = () => (state: RootState) => state[NAMESPACE].rolloverOptions;

export const getPlayerVideoClips = (playerID: PlayerID) => (state: RootState) =>
    _.get(state[NAMESPACE].videoClipsByPlayerID, playerID, {});

export const getPlayerVideoClip = (playerID: PlayerID, clipID: VideoClipID) => (state: RootState) =>
    _.get(state[NAMESPACE].videoClipsByPlayerID, [playerID, clipID], undefined);

export const getPublicAthleteProfileAndClips = (state: RootState) => state[NAMESPACE].athleteProfile;
